import { fetchStatistics, type StreakData } from '@/services/statistics';
import { fetchDayRecords, type DailyRecord } from '@/services/records';

function toDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function getLast7Days(records: Record<string, DailyRecord>) {
  const days = [];
  const today = new Date();
  for (let i = 6; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const dateStr = toDateStr(d);
    const record = records[dateStr];
    days.push({
      date: dateStr,
      label: d.toLocaleDateString('en-US', { weekday: 'short' }).slice(0, 2),
      dayNum: d.getDate(),
      morningStatus: record?.morningStatus || 'pending',
      bedtimeStatus: record?.bedtimeStatus || 'pending',
    });
  }
  return days;
}

function statusDot(status: string) {
  const colors: Record<string, string> = {
    confirmed_early: 'bg-emerald-400',
    on_time: 'bg-emerald-500',
    late: 'bg-amber-400',
    missed: 'bg-rose-400',
    shabbat: 'bg-indigo-300',
    holiday: 'bg-indigo-400',
    pending: 'bg-foreground-200',
  };
  return colors[status] || 'bg-foreground-200';
}

function statusBg(status: string) {
  const colors: Record<string, string> = {
    confirmed_early: 'bg-emerald-100 border-emerald-200',
    on_time: 'bg-emerald-100 border-emerald-200',
    late: 'bg-amber-100 border-amber-200',
    missed: 'bg-rose-100 border-rose-200',
    shabbat: 'bg-indigo-100 border-indigo-200',
    holiday: 'bg-indigo-100 border-indigo-200',
    pending: 'bg-background-200 border-background-300',
  };
  return colors[status] || 'bg-background-200 border-background-300';
}

export default function StreakWidget() {
  const [streakData, setStreakData] = useState<StreakData | null>(null);
  const [records, setRecords] = useState<Record<string, DailyRecord>>({});

  useEffect(() => {
    let mounted = true;
    const today = new Date();
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 6);
    Promise.all([
      fetchStatistics(),
      fetchDayRecords(toDateStr(weekAgo), toDateStr(today)),
    ]).then(([stats, recs]) => {
      if (!mounted) return;
      if (stats) setStreakData(stats.streakData);
      setRecords(recs);
    });
    return () => { mounted = false; };
  }, []);

  const weekDays = getLast7Days(records);

  if (!streakData) {
    return (
      <div className="bg-background-100 rounded-lg border border-background-200/60 overflow-hidden animate-pulse">
        <div className="px-4 py-3 border-b border-background-200/60">
          <div className="h-5 w-24 rounded bg-background-200" />
        </div>
        <div className="p-4"><div className="h-40 rounded bg-background-200/50" /></div>
      </div>
    );
  }

  return (
    <div className="bg-background-100 rounded-lg border border-background-200/60 overflow-hidden">
      <div className="px-4 py-3 border-b border-background-200/60 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 flex items-center justify-center">
            <i className="ri-fire-line text-amber-500 text-lg" />
          </span>
          <h3 className="text-sm font-heading font-semibold text-foreground-950">Streak</h3>
        </div>
        <span className="text-xs text-foreground-500 font-mono">Since {streakData.currentStreakStart}</span>
      </div>

      <div className="p-4">
        {/* Flame + count */}
        <div className="flex items-center justify-center mb-4">
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <span className="w-7 h-7 flex items-center justify-center">
                <i className="ri-fire-fill text-3xl text-gold flame-flicker" />
              </span>
              <span className="font-display text-5xl font-bold text-foreground-950">{streakData.currentStreak}</span>
            </div>
            <p className="text-xs text-foreground-500">day streak</p>
          </div>
        </div>

        {/* 7-day mini tracker */}
        <div className="flex items-center gap-1.5 justify-center mb-3">
          {weekDays.map((day) => (
            <div key={day.date} className="flex flex-col items-center gap-1">
              <span className="text-[10px] text-foreground-400 font-medium">{day.label}</span>
              <div
                className={`w-8 h-8 rounded-md flex items-center justify-center border ${statusBg(day.morningStatus)}`}
                title={`Morning: ${day.morningStatus.replace(/_/g, ' ')}`}
              >
                <div className={`w-2 h-2 rounded-full ${statusDot(day.morningStatus)}`} />
              </div>
              <div
                className={`w-8 h-8 rounded-md flex items-center justify-center border ${statusBg(day.bedtimeStatus)}`}
                title={`Bedtime: ${day.bedtimeStatus.replace(/_/g, ' ')}`}
              >
                <div className={`w-2 h-2 rounded-full ${statusDot(day.bedtimeStatus)}`} />
              </div>
              <span className="text-[10px] text-foreground-400 font-medium">{day.dayNum}</span>
            </div>
          ))}
        </div>

        {/* Longest streak */}
        <div className="flex items-center justify-between pt-3 border-t border-background-200/60">
          <div className="flex items-center gap-2">
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-trophy-line text-amber-500 text-sm" />
            </span>
            <span className="text-xs text-foreground-500">Longest streak</span>
          </div>
          <div className="text-right">
            <span className="text-sm font-semibold text-foreground-900 font-mono">{streakData.longestStreak} days</span>
            <p className="text-[10px] text-foreground-400">{streakData.longestStreakPeriod}</p>
          </div>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-3 flex-wrap mt-3 pt-3 border-t border-background-200/60">
          <div className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
            <span className="text-[10px] text-foreground-500">Early</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
            <span className="text-[10px] text-foreground-500">On Time</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
            <span className="text-[10px] text-foreground-500">Late</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 rounded-full bg-rose-400" />
            <span className="text-[10px] text-foreground-500">Missed</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 rounded-full bg-indigo-300" />
            <span className="text-[10px] text-foreground-500">Shabbat</span>
          </div>
        </div>
      </div>
    </div>
  );
}